// Auth context: wraps Firebase web auth and exposes the signed-in user plus
// the gw `/me` profile (role flags come from here — me.isAdmin etc.). Mirrors
// the shape of ui/src/auth/AuthContext.js so screen logic ports cleanly.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  updatePassword,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [me, setMe] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const refreshMe = useCallback(async () => {
    if (!auth.currentUser) { setMe(null); return null; }
    try {
      const profile = await api.getMe();
      setMe(profile);
      return profile;
    } catch (e) {
      // Blacklisted email, or removed from the community by a moderator
      // (design/029): sign out and say why, instead of an empty app.
      const msg = e?.data?.message || e?.message || '';
      if (e?.status === 403 && /BLACKLISTED:/i.test(msg)) {
        try { await fbSignOut(auth); } catch { /* ignore */ }
        setMe(null);
        alert(msg.replace(/^BLACKLISTED:\s*/i, 'Your account can’t be used: ')
          + '\n\nIf you think this is a mistake, contact support@mediumnumber.com.');
        return null;
      }
      console.warn('[auth] /me failed:', e?.message || e);
      return null;
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        await refreshMe();
      } else {
        setMe(null);
      }
      setInitializing(false);
    });
    return unsub;
  }, [refreshMe]);

  const value = useMemo(() => ({
    user,
    me,
    initializing,
    refreshMe,
    // Derived role flags (gw remains the source of truth on every endpoint).
    isSuperadmin: !!me?.isAdmin,
    signIn: (email, password) => signInWithEmailAndPassword(auth, email, password),
    signUp: (email, password) => createUserWithEmailAndPassword(auth, email, password),
    signOut: () => fbSignOut(auth),
    sendPasswordReset: (email) => sendPasswordResetEmail(auth, email),
    // Re-authenticate with the current password, then set a new one. Used by
    // the forced-change flow for invite-provisioned accounts (design/018 P4).
    // Set a new password right after signing in (no re-entry of the old one).
    // Firebase allows this only shortly after sign-in; otherwise it throws
    // auth/requires-recent-login and the caller asks the user to sign in again.
    setNewPassword: async (newPassword) => {
      const u = auth.currentUser;
      if (!u) throw new Error('Not signed in.');
      await updatePassword(u, newPassword);
    },
    changePassword: async (currentPassword, newPassword) => {
      const u = auth.currentUser;
      if (!u || !u.email) throw new Error('Not signed in.');
      const cred = EmailAuthProvider.credential(u.email, currentPassword);
      await reauthenticateWithCredential(u, cred);
      await updatePassword(u, newPassword);
    },
  }), [user, me, initializing, refreshMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
